<template>
  <div class="working-hours">
    <ul
      v-if="workingHours"
      class="working-hours-list"
    >
      <li
        v-for="(workingHour, index) in workingHours"
        v-if="workingHour"
        :key="index"
        class="row"
      >
        <div class="small-4 medium-4 large-4">
          {{ $t(`workDayOptions.${workingHour.workingDayFrom}`) }} {{ (workingHour.workingDayTo && workingHour.workingDayTo !== workingHour.workingDayFrom) ? `- ${$t(`workDayOptions.${workingHour.workingDayTo}`)}` : '' }}
        </div>
        <div class="small-7 medium-7 large-7">
          {{ customWorkingHourLabel(workingHour.workingHourFrom) }} {{ (workingHour.workingHourTo && workingHour.workingHourTo !== workingHour.workingHourFrom) ? `- ${workingHour.workingHourTo}` : '' }}
        </div>
        <a
          v-show="!disabled"
          class="fa fa-times fa-lg"
          :class="`delete-working-hour-${index}`"
          :aria-hidden="true"
          @click="deleteWorkingHour(index)"
        />
      </li>
    </ul>
    <hr v-if="showWorkingHours && !disabled">

    <div
      v-show="!disabled"
      class="working-hours-add"
    >
      <HKLSelect
        class="workingDayFrom"
        name="workingDayFrom"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours) }"
        :value="workingDayFrom || undefined"
        :options="workingDayFromHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingDayFrom = $event || ''"
      />
      <div class="dash" />
      <HKLSelect
        class="workingDayTo"
        name="workingDayTo"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours)}"
        :disabled="workingDayFrom === 'PH'"
        :value="workingDayTo || undefined"
        :options="workingDayToHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingDayTo = $event || ''"
      />
      <HKLSelect
        class="workingHourFrom"
        name="workingHourFrom"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours) }"
        :value="workingHourFrom || undefined"
        :options="workingHourFromHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingHourFrom = $event || ''"
      />
      <div class="dash" />
      <HKLSelect
        class="workingHourTo"
        name="workingHourTo"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours)}"
        :disabled="workingHourFrom === 'Anytime'"
        :value="workingHourTo || undefined"
        :options="workingHourToHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingHourTo = $event || ''"
      />
      <button
        id="modules-tenders-workingHours-add"
        :disabled="disableButton()"
        type="button"
        role="add-button"
        class="btn btn--darkblue btn-add add-working-hour"
        name="add-working-hour"
        @click="addWorkingHour"
      >
        {{ $t('action.add') }}
      </button>
    </div>
  </div>
</template>

<script>
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import lodash from 'lodash'
import { utils as formUtils } from '@/plugin/InputChange'
import { workingHourOptions, workingDayOptions, workingHourAnytime, workingDayPublicHoliday } from '@/utils/consts'

export default {
  name: 'TenancyWorkingHours',
  components: {
    HKLSelect,
  },
  props: {
    workingHours: {
      type: Array,
      default: [],
    },
    updateValueByPath: {
      type: Function,
    },
    fieldPath: {
      type: Array,
    },
    emptyWorkingHours: {
      type: Boolean,
      default: false,
    },
    isMandatory: {
      type: Boolean,
      default: false,
    },
    checkTimeValidate: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      workingHourOptions,
      workingDayOptions,
      workingDayFrom: '',
      workingDayTo: '',
      workingHourFrom: '',
      workingHourTo: '',
      invalidWorkingHour: false,
      formUtils,
    }
  },

  computed: {
    showWorkingHours () {
      return (this.workingHours || []).length
    },
    workingDayFromHklOptions () {
      return this.mapStringOptions(this.workingDayOptions, this.customWorkingDayLabel)
    },
    workingDayToHklOptions () {
      return this.mapStringOptions(
        this.workingDayOptions.slice(0, this.workingDayOptions.length - 1),
        this.customWorkingDayLabel,
      )
    },
    workingHourFromHklOptions () {
      return this.mapStringOptions(this.workingHourOptions, this.customWorkingHourLabel)
    },
    workingHourToHklOptions () {
      return this.mapStringOptions(
        this.workingHourOptions.slice(1, this.workingHourOptions.length),
        this.customWorkingHourLabel,
      )
    },
  },

  methods: {
    mapStringOptions (options, labelFn) {
      return (options || []).map((value) => ({
        name: labelFn(value),
        value,
      }))
    },
    customWorkingDayLabel (option) {
      return this.$options.filters.customWorkingDayLabel(option)
    },
    customWorkingHourLabel (option) {
      return this.$options.filters.customWorkingHourLabel(option)
    },
    resetWorkingHour () {
      this.workingDayFrom = ''
      this.workingDayTo = ''
      this.workingHourFrom = ''
      this.workingHourTo = ''
      this.invalidWorkingHour = false
    },

    validateMandatory () {
      if (!this.isMandatory) {
        return true
      }
      if (lodash.isEmpty(this.workingHours)) {
        this.invalidWorkingHour = true
        return false
      }
      return true
    },

    validateWorkingHour () {
      const hourFromIndex = this.workingHourOptions.indexOf(this.workingHourFrom)
      const hourToIndex = this.workingHourOptions.indexOf(this.workingHourTo)
      return (hourFromIndex === 0 || hourToIndex - hourFromIndex > 0)
    },

    disableButton () {
      return !(
        (this.workingHourFrom && (this.workingHourFrom === workingHourAnytime || this.workingHourTo)) &&
        (this.workingDayFrom && (this.workingDayFrom === workingDayPublicHoliday || this.workingDayTo))
      )
    },

    addWorkingHour () {
      if (this.checkTimeValidate && !this.validateWorkingHour()) {
        this.invalidWorkingHour = true
        return
      }
      if (this.workingDayFrom === workingDayPublicHoliday) {
        this.workingDayTo = ''
      }
      if (this.workingHourFrom === workingHourAnytime) {
        this.workingHourTo = ''
      }
      const workingHour = {
        workingDayFrom: this.workingDayFrom,
        workingDayTo: this.workingDayTo,
        workingHourFrom: this.workingHourFrom,
        workingHourTo: this.workingHourTo,
      }
      const workingHours = lodash.cloneDeep(this.workingHours)
      workingHours.push(workingHour)
      this.updateValueByPath({
        path: this.fieldPath,
        newValue: workingHours,
      })
      this.resetWorkingHour()
      formUtils.setDirty()
    },

    deleteWorkingHour (index) {
      const workingHours = lodash.cloneDeep(this.workingHours)
      workingHours.splice(index, 1)
      this.updateValueByPath({
        path: this.fieldPath,
        newValue: workingHours,
      })
      formUtils.setDirty()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';

@import '~styles/tender/edit';
@import '~styles/tender/addendum';
@import '~styles/components/working-hours';
</style>

<style lang="scss">
@import '~styles/components/working-hours-add';
</style>
